package one.healthium.onboarding.ui.screens

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEach
import one.healthium.bundle.components.BundleImage
import one.healthium.domain.user.models.prognoseWeightArray
import one.healthium.onboarding.domain.configs.FinalPersonalPlanScreenConfig
import one.healthium.onboarding.domain.models.HappyUsers
import one.healthium.onboarding.domain.models.HighlightList
import one.healthium.onboarding.domain.models.PlanSection
import one.healthium.onboarding.ui.UserDataProvider
import one.healthium.onboarding.ui.UserProfileUiModel
import one.healthium.onboarding.ui.bindPlaceholderValues
import one.healthium.onboarding.ui.components.AnimatableAppText
import one.healthium.onboarding.ui.components.HighlightItem
import one.healthium.onboarding.ui.components.PrognoseWeightChart
import one.healthium.ui.common.utils.toColor
import one.healthium.ui.common.utils.toTextStyle
import one.healthium.ui.components.CtaButton
import one.healthium.ui.components.ReviewsPager
import one.healthium.ui.compose.components.AppButtonHeight
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppText
import one.healthium.ui.compose.theme.AppTheme

interface FinalPersonalPlanEventDelegate {
    fun onNextClicked()
}

@Composable
internal fun FinalPersonalPlanScreen(
    eventDelegate: FinalPersonalPlanEventDelegate,
    screenConfig: FinalPersonalPlanScreenConfig,
) {
    UserDataProvider { userState, _ ->
        AppScaffold {
            FinalPersonalPlanScreenContent(
                screenConfig = screenConfig,
                userState = userState,
                onNextClicked = eventDelegate::onNextClicked,
            )
        }
    }
}

@Composable
internal fun FinalPersonalPlanScreenContent(
    modifier: Modifier = Modifier,
    screenConfig: FinalPersonalPlanScreenConfig,
    userState: UserProfileUiModel,
    onNextClicked: () -> Unit,
) {
    Box {
        Column(
            modifier = modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Header(screenConfig, userState)
            screenConfig.sections.fastForEach { section ->
                PersonalPlanSection(section, userState)
            }
            Spacer(Modifier.height(AppButtonHeight + 100.dp))
        }
        CtaButton(
            modifier = Modifier
                .fillMaxWidth()
                .align(Alignment.BottomCenter)
                .navigationBarsPadding()
                .padding(horizontal = 38.dp, vertical = 24.dp),
            onClick = onNextClicked,
            text = screenConfig.ctaTitle,
        )
    }
}

@Composable
private fun Header(
    screenConfig: FinalPersonalPlanScreenConfig,
    userState: UserProfileUiModel
) {
    Box {
        BundleImage(
            modifier = Modifier
                .fillMaxWidth()
                .height(246.dp),
            name = screenConfig.image,
            contentScale = ContentScale.FillBounds,
        )
        AnimatableAppText(
            modifier = Modifier
                .fillMaxWidth()
                .statusBarsPadding()
                .align(Alignment.Center)
                .padding(bottom = 10.dp)
                .padding(horizontal = 24.dp),
            text = screenConfig.title,
            style = screenConfig.titleStyle.toTextStyle(AppTheme.textStyles.headerRegular),
            color = screenConfig.titleStyle?.color?.toColor() ?: AppTheme.colors.textInverted100,
            textAlign = TextAlign.Center,
            userState = userState,
        )
    }
}

@Composable
private fun PersonalPlanSection(section: PlanSection, userState: UserProfileUiModel) {
    Column(
        modifier = Modifier.fillMaxWidth()
    ) {
        Title(section, userState)
        if (section.weightLossGraph != null) {
            WeightLossGraph(userState, section)
        }
        section.list?.let { list ->
            HighlightList(list, userState)
        }
        section.happyUsers?.let { happyUsers ->
            HappyUsers(happyUsers, userState)
        }
        if (section.reviews.isNotEmpty()) {
            Spacer(Modifier.height(24.dp))
            ReviewsPager(reviews = section.reviews)
            Spacer(Modifier.height(12.dp))
        }
    }
}

@Composable
private fun Title(
    section: PlanSection,
    userState: UserProfileUiModel
) {
    AppText(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 24.dp)
            .padding(top = 24.dp),
        text = section.title?.bindPlaceholderValues(userState),
        style = AppTheme.textStyles.titleSmallBold,
        color = AppTheme.colors.text100,
    )
}

@Composable
private fun WeightLossGraph(
    userState: UserProfileUiModel,
    section: PlanSection
) {
    PrognoseWeightChart(
        modifier = Modifier
            .fillMaxWidth()
            .height(195.dp)
            .padding(horizontal = 24.dp)
            .padding(top = 24.dp),
        prognoseWeight = userState.model.prognoseWeightArray(section.period),
        isMetric = userState.isMetric,
    )
    AppText(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 29.dp)
            .padding(horizontal = 40.dp),
        text = section.weightLossGraph?.bindPlaceholderValues(userState),
        style = AppTheme.textStyles.caption,
        color = AppTheme.colors.text500,
        textAlign = TextAlign.Center,
    )
}

@Composable
private fun HighlightList(
    list: HighlightList,
    userState: UserProfileUiModel
) {
    Spacer(Modifier.height(24.dp))
    list.items.fastForEach { highlightItem ->
        HighlightItem(
            modifier = Modifier.padding(horizontal = 24.dp),
            text = highlightItem.text.bindPlaceholderValues(userState),
            emoji = highlightItem.emoji.value(userState.gender),
            emojiHeight = list.emojiHeight ?: 40,
            textStyle = list.textLabel,
        )
    }
    Spacer(Modifier.height(12.dp))
}

@Composable
private fun HappyUsers(
    happyUsers: HappyUsers,
    userState: UserProfileUiModel
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 24.dp)
            .padding(top = 24.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        BundleImage(name = happyUsers.image)
        Spacer(Modifier.width(12.dp))
        AppText(
            text = happyUsers.title.bindPlaceholderValues(userState = userState),
            style = AppTheme.textStyles.sublineMedium,
            color = AppTheme.colors.text300,
        )
    }
}
